/**
 * Context for opening the contract/strike historical data modal from anywhere (DataTable, DetailedOptionsCharts, etc.).
 */
import React, { createContext, useContext, useState, useCallback } from 'react';
import ContractHistoricalModal, { ContractHistoricalParams } from '../components/ContractHistoricalModal';
import { useTheme } from './ThemeContext';

interface HistoricalModalContextValue {
  openHistorical: (params: ContractHistoricalParams, opts?: { compactChrome?: boolean }) => void;
}

const HistoricalModalContext = createContext<HistoricalModalContextValue | null>(null);

export function HistoricalModalProvider({ children }: { children: React.ReactNode }) {
  const [open, setOpen] = useState(false);
  const [params, setParams] = useState<ContractHistoricalParams | null>(null);
  const [compactChrome, setCompactChrome] = useState(false);
  const { isDarkMode } = useTheme();

  const openHistorical = useCallback((p: ContractHistoricalParams, opts?: { compactChrome?: boolean }) => {
    setParams(p);
    setCompactChrome(Boolean(opts?.compactChrome));
    setOpen(true);
  }, []);

  const onClose = useCallback(() => {
    setOpen(false);
    setParams(null);
    setCompactChrome(false);
  }, []);

  return (
    <HistoricalModalContext.Provider value={{ openHistorical }}>
      {children}
      <ContractHistoricalModal
        open={open}
        onClose={onClose}
        params={params}
        isDarkMode={isDarkMode}
        compactChrome={compactChrome}
      />
    </HistoricalModalContext.Provider>
  );
}

export function useHistoricalModal(): HistoricalModalContextValue {
  const ctx = useContext(HistoricalModalContext);
  if (!ctx) {
    return {
      openHistorical: () => {},
    };
  }
  return ctx;
}
